"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { MobileMenu } from "./MobileMenu";

export function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let ticking = false;

    const updateScrollState = () => {
      setScrolled(window.scrollY > 50);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateScrollState);
      }
    };

    updateScrollState();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      ticking = false;
    };
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 right-0 left-0 z-50 transition-all duration-700 ease-out ${
          scrolled ? "nav-scrolled" : ""
        }`}
      >
        <div
          className={`relative border-b transition-all duration-700 ease-out ${
            scrolled
              ? "border-luxury-stone/20 bg-white/95 shadow-[0_10px_40px_rgba(10,10,10,0.08)] backdrop-blur-[30px]"
              : "border-luxury-stone/10 bg-white/70 backdrop-blur-[20px]"
          }`}
        >
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="flex h-20 items-center justify-between">
              {/* Premium Logo */}
              <Link href="/" className="group flex items-center space-x-3">
                <div className="relative h-12 w-12 flex-shrink-0">
                  <div className="bg-luxury-green/20 group-hover:bg-luxury-green/30 absolute inset-0 rounded-full blur-xl transition-all duration-500" />
                  <Image
                    src="/media/photos/logo.png"
                    alt="Флагман Строй"
                    width={48}
                    height={48}
                    className="relative h-full w-full transform object-contain transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <span className="font-display text-luxury-dark text-xl leading-none font-bold tracking-tight lg:text-2xl">
                    ФЛАГМАН
                  </span>
                  <span className="text-luxury-stone mt-0.5 text-[9px] leading-none tracking-[0.2em] uppercase lg:text-[10px]">
                    Строительство
                  </span>
                </div>
              </Link>

              {/* Desktop Menu */}
              <div className="hidden items-center space-x-1 lg:flex">
                {[
                  { href: "#services", label: "Услуги" },
                  { href: "#portfolio", label: "Портфолио" },
                  { href: "#excellence", label: "Преимущества" },
                  { href: "#process", label: "Процесс" },
                ].map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="text-luxury-stone hover:text-luxury-dark group relative overflow-hidden px-5 py-3 text-sm font-medium tracking-wide transition-colors duration-300"
                  >
                    <span className="relative z-10">{item.label}</span>
                    <div className="from-luxury-green to-luxury-green-light absolute right-5 bottom-3 left-5 h-0.5 origin-left scale-x-0 transform bg-gradient-to-r transition-transform duration-400 group-hover:scale-x-100" />
                  </Link>
                ))}
              </div>

              {/* Premium CTA Button */}
              <div className="hidden lg:block">
                <Link
                  href="#contact"
                  className="group relative inline-flex items-center justify-center overflow-hidden rounded-md px-7 py-2.5 text-sm font-semibold tracking-wider text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  {/* Background gradient */}
                  <span className="from-luxury-green to-luxury-green-dark absolute inset-0 bg-gradient-to-br transition-opacity duration-400 group-hover:opacity-0" />

                  {/* Hover background */}
                  <span className="bg-luxury-dark absolute inset-0 opacity-0 transition-opacity duration-400 group-hover:opacity-100" />

                  {/* Border on hover */}
                  <span
                    className="absolute inset-0 rounded-md opacity-0 transition-opacity duration-400 group-hover:opacity-100"
                    style={{
                      padding: "2px",
                      background:
                        "linear-gradient(135deg, #22C55E, #86EFAC, #22C55E)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />

                  <span className="relative z-10 flex items-center space-x-2">
                    <span>СВЯЗАТЬСЯ</span>
                    <ArrowRight className="h-4 w-4 transform transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="group relative flex h-12 w-12 items-center justify-center lg:hidden"
                aria-label="Открыть меню"
              >
                <div className="relative h-5 w-6">
                  <span className="bg-luxury-dark group-hover:bg-luxury-green absolute top-0 left-0 h-0.5 w-full rounded-full transition-all duration-300" />
                  <span className="bg-luxury-dark group-hover:bg-luxury-green absolute top-1/2 left-0 h-0.5 w-full -translate-y-1/2 rounded-full transition-all duration-300" />
                  <span className="bg-luxury-dark group-hover:bg-luxury-green absolute bottom-0 left-0 h-0.5 w-full rounded-full transition-all duration-300" />
                </div>
              </button>
            </div>
          </div>

          {/* Animated Border */}
          <div
            className={`via-luxury-green pointer-events-none absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-transparent to-transparent transition-all duration-800 ${
              scrolled ? "w-full" : "w-0"
            }`}
          />
        </div>
      </nav>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </>
  );
}
